import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Mail, Send, MessageSquare, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_EMAIL = 'admin@eatfromlocal.com';

export const ContactPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 10) {
      toast.error('Message must be at least 10 characters');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message received — we'll be in touch soon!");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ backgroundColor: '#F9F6F0', minHeight: '100vh' }} data-testid="contact-page">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-16 sm:pb-24">
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-14 items-start">
          {/* Left: Info */}
          <div className="lg:col-span-2">
            <p
              className="uppercase mb-3"
              style={{ fontFamily: 'Manrope', fontSize: '12px', fontWeight: 600, letterSpacing: '0.18em', color: '#D95A41' }}
            >
              Get in touch
            </p>
            <h1
              style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }}
              className="text-4xl sm:text-5xl lg:text-6xl leading-tight mb-4"
            >
              Something on your mind?
            </h1>
            <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }} className="text-base sm:text-lg mb-8 leading-relaxed">
              Whether you're a home cook with a question, a buyer with feedback, or a council checking on the community — we read every message and reply within 24 hours.
            </p>

            <div className="space-y-5">
              <a
                href={`mailto:${ADMIN_EMAIL}`}
                className="flex items-start gap-3 group"
                data-testid="contact-email-link"
              >
                <span
                  className="flex-shrink-0 flex items-center justify-center rounded-full"
                  style={{ width: 40, height: 40, backgroundColor: '#FCE7DE', color: '#8A2E17' }}
                >
                  <Mail size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <div style={{ fontFamily: 'Manrope', fontSize: '12px', fontWeight: 600, color: '#5C5C5C', letterSpacing: '0.08em' }} className="uppercase mb-0.5">
                    Email us
                  </div>
                  <div
                    style={{ fontFamily: 'Manrope', fontWeight: 600, color: '#1A1A1A' }}
                    className="group-hover:underline break-all"
                    data-testid="contact-email-value"
                  >
                    {ADMIN_EMAIL}
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-3">
                <span
                  className="flex-shrink-0 flex items-center justify-center rounded-full"
                  style={{ width: 40, height: 40, backgroundColor: '#E5EFE4', color: '#2E5C41' }}
                >
                  <MessageSquare size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <div style={{ fontFamily: 'Manrope', fontSize: '12px', fontWeight: 600, color: '#5C5C5C', letterSpacing: '0.08em' }} className="uppercase mb-0.5">
                    Support hours
                  </div>
                  <div style={{ fontFamily: 'Manrope', fontWeight: 600, color: '#1A1A1A' }}>
                    Mon – Sat, 9am – 6pm AEST
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <span
                  className="flex-shrink-0 flex items-center justify-center rounded-full"
                  style={{ width: 40, height: 40, backgroundColor: '#F2E4C9', color: '#7A5A0F' }}
                >
                  <MapPin size={18} strokeWidth={1.5} />
                </span>
                <div>
                  <div style={{ fontFamily: 'Manrope', fontSize: '12px', fontWeight: 600, color: '#5C5C5C', letterSpacing: '0.08em' }} className="uppercase mb-0.5">
                    Community
                  </div>
                  <div style={{ fontFamily: 'Manrope', fontWeight: 600, color: '#1A1A1A' }}>
                    Neighbours cooking for neighbours, across Australia
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="lg:col-span-3">
            {sent ? (
              <div
                className="rounded-md border border-[#E5E0D8] p-8 sm:p-12 text-center"
                style={{ backgroundColor: '#FFFFFF' }}
                data-testid="contact-success"
              >
                <div
                  className="mx-auto mb-4 rounded-full flex items-center justify-center"
                  style={{ width: 64, height: 64, backgroundColor: '#E5EFE4', color: '#2E5C41' }}
                >
                  <Send size={26} strokeWidth={1.5} />
                </div>
                <h2 style={{ fontFamily: 'Cormorant Garamond', fontWeight: 600, color: '#1A1A1A' }} className="text-3xl mb-3">
                  Message received.
                </h2>
                <p style={{ fontFamily: 'Manrope', color: '#5C5C5C' }} className="mb-6">
                  Thanks {form.name.split(' ')[0] || 'friend'} — we've got it and will reply to <strong style={{ color: '#1A1A1A' }}>{form.email}</strong> within 24 hours.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    variant="outline"
                    onClick={() => { setSent(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                    style={{ fontFamily: 'Manrope', fontWeight: 600 }}
                    data-testid="send-another-btn"
                  >
                    Send another message
                  </Button>
                  <Button
                    onClick={() => navigate('/')}
                    className="rounded-md"
                    style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600 }}
                  >
                    Back to home
                  </Button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="rounded-md border border-[#E5E0D8] p-6 sm:p-8 lg:p-10 space-y-5"
                style={{ backgroundColor: '#FFFFFF' }}
                data-testid="contact-form"
              >
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">
                      Your name *
                    </label>
                    <input
                      name="name"
                      type="text"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Jane Doe"
                      className="w-full px-4 py-2.5 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                      style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                      data-testid="contact-name-input"
                    />
                  </div>
                  <div>
                    <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">
                      Email address *
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="you@example.com"
                      className="w-full px-4 py-2.5 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                      style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                      data-testid="contact-email-input"
                    />
                  </div>
                </div>
                <div>
                  <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">
                    Subject
                  </label>
                  <input
                    name="subject"
                    type="text"
                    value={form.subject}
                    onChange={handleChange}
                    placeholder="How can we help?"
                    className="w-full px-4 py-2.5 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                    style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0' }}
                    data-testid="contact-subject-input"
                  />
                </div>
                <div>
                  <label style={{ fontFamily: 'Manrope', fontSize: '13px', fontWeight: 600, color: '#5C5C5C' }} className="block mb-2">
                    Your message *
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={6}
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us what's up…"
                    className="w-full px-4 py-3 border border-[#E5E0D8] rounded-md focus:outline-none focus:ring-2 focus:ring-[#D95A41]"
                    style={{ fontFamily: 'Manrope', backgroundColor: '#F9F6F0', lineHeight: 1.6, resize: 'vertical' }}
                    data-testid="contact-message-input"
                  />
                  <p style={{ fontFamily: 'Manrope', fontSize: '12px', color: '#8A8073' }} className="mt-1">
                    Minimum 10 characters
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-3 pt-2">
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="rounded-md w-full sm:w-auto"
                    style={{ backgroundColor: '#D95A41', fontFamily: 'Manrope', fontWeight: 600, minWidth: 160 }}
                    data-testid="contact-submit-btn"
                  >
                    <Send size={16} className="mr-2" strokeWidth={1.5} />
                    {submitting ? 'Sending…' : 'Send message'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
